const WIN_TO_IANA = {
    'Hawaiian Standard Time': 'Pacific/Honolulu',
    'Alaskan Standard Time': 'America/Anchorage',
    'Pacific Standard Time': 'America/Los_Angeles',
    'US Mountain Standard Time': 'America/Phoenix',
    'Mountain Standard Time': 'America/Denver',
    'Central Standard Time': 'America/Chicago',
    'Eastern Standard Time': 'America/New_York',
    'Central Standard Time (Mexico)': 'America/Mexico_City',
    'SA Pacific Standard Time': 'America/Bogota',
    'E. South America Standard Time': 'America/Sao_Paulo',
    UTC: 'UTC',
    'GMT Standard Time': 'Europe/London',
    'Romance Standard Time': 'Europe/Paris',
    'GTB Standard Time': 'Europe/Athens',
    'Russian Standard Time': 'Europe/Moscow',
    'Arabian Standard Time': 'Asia/Dubai',
    'Pakistan Standard Time': 'Asia/Karachi',
    'India Standard Time': 'Asia/Kolkata',
    'Bangladesh Standard Time': 'Asia/Dhaka',
    'SE Asia Standard Time': 'Asia/Bangkok',
    'Singapore Standard Time': 'Asia/Singapore',
    'China Standard Time': 'Asia/Shanghai',
    'Tokyo Standard Time': 'Asia/Tokyo',
    'AUS Eastern Standard Time': 'Australia/Sydney',
    'New Zealand Standard Time': 'Pacific/Auckland',
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const CADENCE_WEEKLY = 'Weekly'
export const CADENCE_MONTHLY = 'Monthly'

export const WINDOW_KIND = {
    SinceLastFire: 'SinceLastFire',
    CurrentWeek: 'CurrentWeek',
    PreviousWeek: 'PreviousWeek',
    RollingDays: 'RollingDays',
}

export const WEEK_STARTS_ON = {
    Monday: 'Monday',
    Sunday: 'Sunday',
}

export const WINDOW_KIND_OPTIONS = [
    {
        key: WINDOW_KIND.SinceLastFire,
        value: WINDOW_KIND.SinceLastFire,
        text: 'Since last email — rolling (default)',
    },
    {
        key: WINDOW_KIND.CurrentWeek,
        value: WINDOW_KIND.CurrentWeek,
        text: 'This week so far',
    },
    {
        key: WINDOW_KIND.PreviousWeek,
        value: WINDOW_KIND.PreviousWeek,
        text: 'Last full week',
    },
    {
        key: WINDOW_KIND.RollingDays,
        value: WINDOW_KIND.RollingDays,
        text: 'Last N days…',
    },
]

export const WINDOW_KIND_META = {
    [WINDOW_KIND.SinceLastFire]: {
        label: 'Since last email (rolling)',
        help: 'From the moment the previous email fired, up to the moment this one does. No gaps, no overlap. Best for "keep the stream flowing".',
        showRolling: false,
        showWeek: false,
    },
    [WINDOW_KIND.CurrentWeek]: {
        label: 'This week so far',
        help: 'From the start of the current week in your company time zone, up to the moment the digest fires.',
        showRolling: false,
        showWeek: true,
    },
    [WINDOW_KIND.PreviousWeek]: {
        label: 'Last full week',
        help: 'The complete week just gone (Monday 00:00 through Sunday 24:00 by default). Useful for weekly recaps posted mid-week.',
        showRolling: false,
        showWeek: true,
    },
    [WINDOW_KIND.RollingDays]: {
        label: 'Last N days',
        help: 'A rolling window ending at the fire time. Simplest for "give me the last 3 days" without worrying about week starts.',
        showRolling: true,
        showWeek: false,
    },
}

export const STATUS_OPTIONS = [
    { key: 'all', text: 'All statuses', value: 'all' },
    { key: 'active', text: 'Active', value: 'active' },
    { key: 'held', text: 'On hold', value: 'held' },
]

export const RUN_STATUS_OPTIONS = [
    { key: 'all', text: 'All statuses', value: 'all' },
    { key: 'Delivered', text: 'Delivered', value: 'Delivered' },
    { key: 'Skipped-Empty', text: 'Skipped - empty', value: 'Skipped-Empty' },
    { key: 'Skipped-Held', text: 'Skipped - held', value: 'Skipped-Held' },
    {
        key: 'Skipped-NoRecipients',
        text: 'Skipped - no recipients',
        value: 'Skipped-NoRecipients',
    },
    { key: 'Partial', text: 'Partial', value: 'Partial' },
    { key: 'Failed', text: 'Failed', value: 'Failed' },
    { key: 'Failed-Skipped', text: 'Failed - skipped', value: 'Failed-Skipped' },
]

export const SEND_MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => ({
    key: i,
    text: String(i).padStart(2, '0'),
    value: i,
}))

export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
    key: i,
    text: String(i).padStart(2, '0'),
    value: i,
}))

/** Question fields allowed on each template email, not across every template. */
export const MAX_TEMPLATE_COLUMNS = 7

export const BUILTIN_EMAIL_COLUMNS = [
    {
        key: 'audit',
        title: 'Audit',
        help: 'Audit name, linked to the report.',
        defaultLabel: 'Audit',
        visible: true,
    },
    {
        key: 'auditor',
        title: 'Auditor',
        help: 'Who completed it.',
        defaultLabel: 'Auditor',
        visible: true,
    },
    {
        key: 'submitted',
        title: 'Submitted',
        help: 'Date completed.',
        defaultLabel: 'Submitted',
        visible: true,
    },
    {
        key: 'score',
        title: 'Score',
        help: 'Percentage vs target.',
        defaultLabel: 'Score',
        visible: true,
    },
    {
        key: 'template',
        title: 'Template',
        help: 'Template the audit came from.',
        defaultLabel: 'Template',
        visible: true,
    },
    {
        key: 'branch',
        title: 'Branch',
        help: 'Location of the audit.',
        defaultLabel: 'Branch',
        visible: false,
    },
    {
        key: 'link',
        title: 'Report link',
        help: '"View" link. Header is blank by default.',
        defaultLabel: '',
        placeholder: '(blank)',
        visible: true,
    },
]

const builtinByKey = Object.fromEntries(
    BUILTIN_EMAIL_COLUMNS.map(col => [col.key, col]),
)

export const defaultEmailColumns = () =>
    BUILTIN_EMAIL_COLUMNS.map(col => ({
        key: col.key,
        builtin: true,
        locked: Boolean(col.locked),
        visible: col.visible,
        label: '',
    }))

const questionColumnKey = questionId => `question:${questionId}`

export const columnHeading = column => {
    const typed = String(column?.label || '').trim()
    if (typed) return typed
    if (column?.builtin) return builtinByKey[column.key]?.defaultLabel || ''
    return column?.questionText || ''
}

export const columnsFromSchedule = schedule => {
    const raw = schedule?.Columns || schedule?.columns
    if (!Array.isArray(raw) || raw.length === 0) return defaultEmailColumns()

    const sorted = [...raw].sort(
        (a, b) =>
            Number(a.DisplayOrder ?? a.displayOrder ?? 0) -
            Number(b.DisplayOrder ?? b.displayOrder ?? 0),
    )

    const mapped = sorted.map(item => {
        const key = String(item.ColumnKey || item.columnKey || '')
        const builtin = builtinByKey[key]
        const questionId = item.QuestionId ?? item.questionId
        const label = item.Label ?? item.label
        if (builtin) {
            return {
                key,
                builtin: true,
                locked: Boolean(builtin.locked),
                visible:
                    builtin.locked ||
                    item.IsVisible === true ||
                    item.isVisible === true,
                label: label == null ? '' : String(label),
                showPhotos: item.ShowPhotos === true || item.showPhotos === true,
            }
        }
        // The server returns Label = question text for a field that was never renamed.
        const questionText = item.QuestionText || item.questionText || ''
        const ownLabel =
            label != null && questionText && String(label) === questionText ? '' : label
        const parsedId = key.startsWith('question:')
            ? Number(key.slice('question:'.length))
            : Number(questionId)
        return {
            key: parsedId ? questionColumnKey(parsedId) : key || questionColumnKey(parsedId),
            builtin: false,
            locked: false,
            visible: item.IsVisible !== false && item.isVisible !== false,
            label: ownLabel == null ? '' : String(ownLabel),
            questionId: parsedId || null,
            templateId: item.TemplateId ?? item.templateId ?? null,
            questionText:
                item.QuestionText ||
                item.questionText ||
                (label ? String(label) : 'Template field'),
            templateName: item.TemplateName || item.templateName || '',
            showPhotos: item.ShowPhotos === true || item.showPhotos === true,
        }
    })

    const present = new Set(mapped.map(col => col.key))
    const missingBuiltins = defaultEmailColumns().filter(col => !present.has(col.key))
    const hasBuiltin = mapped.some(col => col.builtin)
    if (!hasBuiltin) return [...missingBuiltins, ...mapped]
    return [...mapped, ...missingBuiltins]
}

/** Full set for POST .../columns/save. Blank built-in label → null (server default). */
export const columnsToPayload = columns =>
    (columns || []).map((column, index) => {
        const typed = String(column.label || '').trim()
        const builtin = column.builtin ? builtinByKey[column.key] : null
        let label = typed || column.questionText || null
        if (column.builtin) {
            const useDefault = !typed || typed === builtin.defaultLabel
            label = useDefault ? null : typed
        }
        const row = {
            ColumnKey: column.key,
            IsVisible: column.locked ? true : Boolean(column.visible),
            Label: label,
            DisplayOrder: (index + 1) * 10,
            ShowPhotos: Boolean(column.showPhotos),
        }
        if (!column.builtin && column.questionId != null && column.questionId !== '') {
            const questionId = Number(column.questionId)
            if (Number.isFinite(questionId)) {
                row.QuestionId = questionId
            }
            const templateId = Number(column.templateId)
            if (Number.isFinite(templateId)) row.TemplateId = templateId
        }
        return row
    })

export const emptyFormState = (defaultTimezone = '') => ({
    scheduleName: '',
    isActive: true,
    onHold: false,
    sendEmptyDigest: false,
    windowKind: WINDOW_KIND.SinceLastFire,
    weekStartsOn: WEEK_STARTS_ON.Monday,
    rollingDays: 3,
    cadence: CADENCE_WEEKLY,
    weekDays: [1, 4],
    monthDays: [1],
    repeatInterval: 1,
    sendHour: 8,
    sendMinute: 0,
    timeZone: defaultTimezone || 'America/Chicago',
    includeAllTemplates: false,
    templateIds: [],
    templateOrderSaved: false,
    includeAllAuditors: true,
    sendAnyStatus: false,
    auditorUserIds: [],
    userIds: [],
    externalEmails: '',
    columns: defaultEmailColumns(),
})

export const toIanaTimezone = timezone =>
    WIN_TO_IANA[timezone] || timezone || 'America/Chicago'

export const getViewerTimezone = fallbackTimezone => {
    if (typeof Intl !== 'undefined') {
        const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        if (localTimezone) return localTimezone
    }
    return toIanaTimezone(fallbackTimezone)
}

export const getTimezoneAbbr = (timezone, date = new Date()) => {
    const iana = toIanaTimezone(timezone)
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: iana,
        timeZoneName: 'short',
    }).formatToParts(date)
    return parts.find(p => p.type === 'timeZoneName')?.value || 'CT'
}

export const formatUtcInTimezone = (utcIso, timezone, options = {}) => {
    if (!utcIso) return '—'
    const raw = String(utcIso)
    const date = new Date(
        raw.includes('T') && !/Z$|[+-]\d{2}:\d{2}$/.test(raw)
            ? `${raw}Z`
            : raw,
    )
    if (Number.isNaN(date.getTime())) return '—'
    return formatDateParts(date, timezone, options)
}

const formatDateParts = (date, timezone, options = {}) => {
    const iana = toIanaTimezone(timezone)
    return new Intl.DateTimeFormat('en-US', {
        timeZone: iana,
        weekday: options.weekday ? 'short' : undefined,
        month: 'short',
        day: 'numeric',
        year: options.year ? 'numeric' : undefined,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...options,
    }).format(date)
}

export const formatFireWhen = (utcIso, timezone) => {
    if (!utcIso) return ''
    const date = new Date(utcIso)
    const iana = toIanaTimezone(timezone)
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: iana,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).formatToParts(date)
    const get = type => parts.find(p => p.type === type)?.value || ''
    const abbr = getTimezoneAbbr(timezone, date)
    return `${get('weekday')} ${get('month')} ${get('day')} ${get('hour')}:${get('minute')} ${get('dayPeriod')} ${abbr}`
}

const parseUtcLike = utcIso => {
    if (!utcIso) return null
    const raw = String(utcIso)
    const date = new Date(
        raw.includes('T') && !/Z$|[+-]\d{2}:\d{2}$/.test(raw)
            ? `${raw}Z`
            : raw,
    )
    return Number.isNaN(date.getTime()) ? null : date
}

const getWindowDateParts = (utcIso, timezone) => {
    const date = parseUtcLike(utcIso)
    if (!date) return null
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: toIanaTimezone(timezone),
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).formatToParts(date)
    const get = type => parts.find(p => p.type === type)?.value || ''
    return {
        month: get('month'),
        day: get('day'),
        year: get('year'),
    }
}

const formatWindowDateRange = (startUtc, endUtc, timezone) => {
    const start = getWindowDateParts(startUtc, timezone)
    const end = getWindowDateParts(endUtc, timezone)
    if (!start || !end) return '—'
    if (
        start.month === end.month &&
        start.day === end.day &&
        start.year === end.year
    ) {
        return `${start.month} ${start.day}`
    }
    if (start.year !== end.year) {
        return `${start.month} ${start.day}, ${start.year} – ${end.month} ${end.day}, ${end.year}`
    }
    if (start.month === end.month) {
        return `${start.month} ${start.day} – ${end.month} ${end.day}`
    }
    return `${start.month} ${start.day} – ${end.month} ${end.day}`
}

export const formatRunWindow = (run, timezone) => {
    if (!run) return '—'
    if (run.WindowLabel) return run.WindowLabel
    if (!run.WindowStartUtc || !run.WindowEndUtc) return '—'

    return formatWindowDateRange(run.WindowStartUtc, run.WindowEndUtc, timezone)
}

export const parseExternalEmails = raw => {
    if (!raw || !String(raw).trim()) return []
    return String(raw)
        .split(/[;,\n]/)
        .map(s => s.trim())
        .filter(Boolean)
}

export const validateExternalEmails = raw => {
    const emails = parseExternalEmails(raw)
    const invalid = emails.filter(e => !EMAIL_RE.test(e))
    return { emails, invalid }
}

export const formatDayLabels = weekDays => {
    const sorted = [...(weekDays || [])].sort((a, b) => a - b)
    return sorted.map(d => DAY_LABELS[d - 1]).filter(Boolean).join(' + ')
}

export const countRecipients = (userIds, externalEmails) => {
    const users = (userIds || []).length
    const emails = parseExternalEmails(externalEmails).length
    if (users && emails) return `${users} users + ${emails} emails`
    if (users) return `${users} users`
    if (emails) return `${emails} emails`
    return '0'
}

export const computeNextSendPreview = (form, timezone) => {
    const now = new Date()
    const iana = toIanaTimezone(timezone)
    const { cadence, weekDays, monthDays, sendHour, sendMinute, repeatInterval } =
        form

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: iana,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })

    const getZonedParts = date => {
        const parts = formatter.formatToParts(date)
        const get = type => Number(parts.find(p => p.type === type)?.value || 0)
        return {
            year: get('year'),
            month: get('month'),
            day: get('day'),
            hour: get('hour'),
            minute: get('minute'),
        }
    }

    const makeUtcFromZoned = (year, month, day, hour, minute) => {
        const guess = new Date(Date.UTC(year, month - 1, day, hour, minute))
        const parts = getZonedParts(guess)
        const offset =
            Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute) -
            guess.getTime()
        return new Date(
            Date.UTC(year, month - 1, day, hour, minute) - offset,
        )
    }

    const candidates = []
    if (cadence === CADENCE_WEEKLY) {
        const days = weekDays?.length ? weekDays : [1]
        for (let offset = 0; offset < 14; offset += 1) {
            const probe = new Date(now.getTime() + offset * 86400000)
            const parts = getZonedParts(probe)
            const jsDay = probe.getUTCDay()
            const isoDay = jsDay === 0 ? 7 : jsDay
            if (days.includes(isoDay)) {
                const candidate = makeUtcFromZoned(
                    parts.year,
                    parts.month,
                    parts.day,
                    sendHour,
                    sendMinute,
                )
                if (candidate > now) candidates.push(candidate)
            }
        }
    } else {
        const days = monthDays?.length ? monthDays : [1]
        for (let m = 0; m < 3; m += 1) {
            const probe = new Date(now.getFullYear(), now.getMonth() + m, 1)
            const parts = getZonedParts(probe)
            days.forEach(dayNum => {
                let day = dayNum
                if (dayNum === 32) {
                    day = new Date(parts.year, parts.month, 0).getDate()
                }
                const candidate = makeUtcFromZoned(
                    parts.year,
                    parts.month,
                    day,
                    sendHour,
                    sendMinute,
                )
                if (candidate > now) candidates.push(candidate)
            })
        }
    }

    candidates.sort((a, b) => a - b)
    const next = candidates[0]
    if (!next) return '—'

    const abbr = getTimezoneAbbr(timezone, next)
    const label = formatUtcInTimezone(next.toISOString(), timezone, {
        weekday: 'short',
        year: 'numeric',
    })
    let interval = ''
    if (repeatInterval > 1) {
        interval =
            cadence === CADENCE_WEEKLY
                ? `Every ${repeatInterval} weeks · `
                : `Every ${repeatInterval} months · `
    }
    return `${interval}${label} ${abbr}`
}

const formatCadencePreview = form => {
    if (form.cadence === CADENCE_WEEKLY) {
        if (form.weekDays?.length > 1) {
            return `Weekly · ${form.weekDays.length}×/wk`
        }
        return 'Weekly'
    }
    if (form.monthDays?.length > 1) {
        return `Monthly · ${form.monthDays.length} days`
    }
    return 'Monthly'
}

const pad2 = n => String(n).padStart(2, '0')

const getZonedYmdHm = (date, iana) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: iana,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        weekday: 'short',
        hour12: false,
    }).formatToParts(date)
    const get = type => parts.find(p => p.type === type)?.value || ''
    return {
        year: Number(get('year')),
        month: Number(get('month')),
        day: Number(get('day')),
        hour: Number(get('hour') === '24' ? 0 : get('hour')),
        minute: Number(get('minute')),
        weekday: get('weekday'),
    }
}

const formatWindowInstant = (date, timezone) => {
    const iana = toIanaTimezone(timezone)
    const p = getZonedYmdHm(date, iana)
    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ]
    return `${p.weekday} ${months[p.month - 1]} ${pad2(p.day)}, ${pad2(p.hour)}:${pad2(p.minute)}`
}

const startOfWeekParts = (fireDate, timezone, weekStartsOn) => {
    const iana = toIanaTimezone(timezone)
    const z = getZonedYmdHm(fireDate, iana)
    const jsWeekday = new Date(Date.UTC(z.year, z.month - 1, z.day, 12, 0)).getUTCDay()
    const startsSunday = weekStartsOn === WEEK_STARTS_ON.Sunday
    const daysSinceStart = startsSunday ? jsWeekday : (jsWeekday + 6) % 7
    const startUtc = new Date(Date.UTC(z.year, z.month - 1, z.day - daysSinceStart, 12, 0))
    return getZonedYmdHm(startUtc, iana)
}

const formatDayMidnight = parts => {
    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ]
    return `${parts.weekday} ${months[parts.month - 1]} ${pad2(parts.day)}, 00:00`
}

export const computeWindowPreview = (form, timezone) => {
    const kind = form.windowKind || WINDOW_KIND.SinceLastFire
    const meta =
        WINDOW_KIND_META[kind] || WINDOW_KIND_META[WINDOW_KIND.SinceLastFire]
    const weekStartsOn = form.weekStartsOn || WEEK_STARTS_ON.Monday
    const abbr = getTimezoneAbbr(timezone)
    const fireApprox = new Date()
    const endLabel = formatWindowInstant(fireApprox, timezone)

    if (kind === WINDOW_KIND.SinceLastFire) {
        const prev = new Date(fireApprox.getTime() - 7 * 86400000)
        return {
            kindLabel: meta.label,
            weekStarts: '—',
            preview: `Prev fire (${formatWindowInstant(prev, timezone)} ${abbr}) → ${endLabel} ${abbr}`,
            showWeek: false,
            showRolling: false,
            help: meta.help,
        }
    }

    if (kind === WINDOW_KIND.RollingDays) {
        const n = Math.max(1, Math.min(365, Number(form.rollingDays) || 3))
        const back = new Date(fireApprox.getTime() - n * 86400000)
        return {
            kindLabel: meta.label,
            weekStarts: '—',
            preview: `${formatWindowInstant(back, timezone)} → ${endLabel} ${abbr}`,
            showWeek: false,
            showRolling: true,
            help: meta.help,
            rollingDays: n,
        }
    }

    const weekStart = startOfWeekParts(fireApprox, timezone, weekStartsOn)

    if (kind === WINDOW_KIND.CurrentWeek) {
        return {
            kindLabel: meta.label,
            weekStarts: weekStartsOn,
            preview: `${formatDayMidnight(weekStart)} → ${endLabel} ${abbr}`,
            showWeek: true,
            showRolling: false,
            help: meta.help,
        }
    }

    // PreviousWeek
    const iana = toIanaTimezone(timezone)
    const prevStartUtc = new Date(
        Date.UTC(weekStart.year, weekStart.month - 1, weekStart.day - 7, 12, 0),
    )
    const prevEndUtc = new Date(
        Date.UTC(weekStart.year, weekStart.month - 1, weekStart.day, 12, 0),
    )
    const prevStart = getZonedYmdHm(prevStartUtc, iana)
    const prevEnd = getZonedYmdHm(prevEndUtc, iana)
    return {
        kindLabel: meta.label,
        weekStarts: weekStartsOn,
        preview: `${formatDayMidnight(prevStart)} → ${formatDayMidnight(prevEnd)} ${abbr}`,
        showWeek: true,
        showRolling: false,
        help: meta.help,
    }
}

export const buildLivePreview = (form, timezone) => {
    const cadence = formatCadencePreview(form)
    const windowPreview = computeWindowPreview(form, timezone)

    return {
        cadence,
        days:
            form.cadence === CADENCE_WEEKLY
                ? formatDayLabels(form.weekDays)
                : (form.monthDays || [])
                      .map(d => (d === 32 ? 'Last day' : `Day ${d}`))
                      .join(' + '),
        nextSend: computeNextSendPreview(form, timezone),
        recipients: countRecipients(form.userIds, form.externalEmails),
        ifEmpty: form.sendEmptyDigest ? 'Send' : 'Skip',
        window: windowPreview,
    }
}

export const scheduleToForm = (schedule, companyTimezone) => {
    // GET returns { Success, Schedule }; list items are already flat
    const s = schedule?.Schedule || schedule
    if (!s) {
        return emptyFormState(toIanaTimezone(companyTimezone))
    }

    const userRecipients = (s.Recipients || []).filter(r => r.UserId)
    const emailRecipients = (s.Recipients || []).filter(r => r.Email)
    const scheduleTz =
        s.IanaName ||
        s.TimeZoneIana ||
        (s.TimeZone || s.WindowsId || s.TimeZoneId
            ? toIanaTimezone(s.TimeZone || s.WindowsId || s.TimeZoneId)
            : '') ||
        toIanaTimezone(companyTimezone)

    return {
        scheduleName: s.ScheduleName || '',
        isActive: s.IsActive !== false,
        onHold: s.OnHold === true,
        sendEmptyDigest: s.SendEmptyDigest === true,
        windowKind: s.WindowKind || WINDOW_KIND.SinceLastFire,
        weekStartsOn: s.WeekStartsOn || WEEK_STARTS_ON.Monday,
        rollingDays: (() => {
            if (s.RollingDaysCount > 0) return Number(s.RollingDaysCount)
            if (s.RollingDays > 0) return Number(s.RollingDays)
            return 3
        })(),
        cadence: s.Cadence || CADENCE_WEEKLY,
        weekDays: s.WeekDays?.length ? s.WeekDays.map(Number) : [1],
        monthDays: s.MonthDays?.length ? s.MonthDays.map(Number) : [1],
        repeatInterval: s.RepeatInterval || 1,
        sendHour: s.SendHour ?? 8,
        sendMinute: s.SendMinute ?? 0,
        timeZone: scheduleTz,
        includeAllTemplates: s.IncludeAllTemplates === true,
        templateIds: (s.TemplateIds || []).map(Number),
        templateOrderSaved: s.TemplateOrderSaved === true,
        includeAllAuditors: s.IncludeAllAuditors !== false,
        sendAnyStatus: s.SendAnyStatus === true,
        auditorUserIds: (s.AuditorUserIds || []).map(Number),
        userIds: userRecipients.map(r => Number(r.UserId)),
        externalEmails: emailRecipients.map(r => r.Email).join('; '),
        columns: columnsFromSchedule(s),
    }
}

/** Map selected IANA id → WindowsId for API (company TZ style). */
const IANA_TO_WIN = {
    'Pacific/Honolulu': 'Hawaiian Standard Time',
    'America/Anchorage': 'Alaskan Standard Time',
    'America/Los_Angeles': 'Pacific Standard Time',
    'America/Phoenix': 'US Mountain Standard Time',
    'America/Denver': 'Mountain Standard Time',
    'America/Chicago': 'Central Standard Time',
    'America/New_York': 'Eastern Standard Time',
    'America/Toronto': 'Eastern Standard Time',
    'America/Mexico_City': 'Central Standard Time (Mexico)',
    'America/Bogota': 'SA Pacific Standard Time',
    'America/Sao_Paulo': 'E. South America Standard Time',
    UTC: 'UTC',
    'Europe/London': 'GMT Standard Time',
    'Europe/Paris': 'Romance Standard Time',
    'Europe/Athens': 'GTB Standard Time',
    'Europe/Moscow': 'Russian Standard Time',
    'Asia/Dubai': 'Arabian Standard Time',
    'Asia/Karachi': 'Pakistan Standard Time',
    'Asia/Kolkata': 'India Standard Time',
    'Asia/Dhaka': 'Bangladesh Standard Time',
    'Asia/Bangkok': 'SE Asia Standard Time',
    'Asia/Singapore': 'Singapore Standard Time',
    'Asia/Shanghai': 'China Standard Time',
    'Asia/Tokyo': 'Tokyo Standard Time',
    'Australia/Sydney': 'AUS Eastern Standard Time',
    'Pacific/Auckland': 'New Zealand Standard Time',
}

export const ianaToWindowsId = iana =>
    IANA_TO_WIN[iana] || 'Central Standard Time'

export const formToPayload = form => {
    const isWeekly = form.cadence === CADENCE_WEEKLY
    const includeAllTemplates = Boolean(form.includeAllTemplates)
    const includeAllAuditors = Boolean(form.includeAllAuditors)
    const iana = form.timeZone || 'America/Chicago'
    const windowsId = ianaToWindowsId(iana)
    const windowKind = form.windowKind || WINDOW_KIND.SinceLastFire
    const needsWeekStart =
        windowKind === WINDOW_KIND.CurrentWeek ||
        windowKind === WINDOW_KIND.PreviousWeek
    const needsRolling = windowKind === WINDOW_KIND.RollingDays

    return {
        ScheduleName: form.scheduleName?.trim(),
        IsActive: Boolean(form.isActive),
        OnHold: Boolean(form.onHold),
        SendEmptyDigest: Boolean(form.sendEmptyDigest),
        WindowKind: windowKind,
        WeekStartsOn: needsWeekStart
            ? form.weekStartsOn || WEEK_STARTS_ON.Monday
            : null,
        RollingDaysCount: needsRolling
            ? Math.max(1, Math.min(365, Number(form.rollingDays) || 3))
            : null,
        Cadence: form.cadence,
        WeekDays: isWeekly ? (form.weekDays || []).map(Number) : null,
        MonthDays: isWeekly ? null : (form.monthDays || []).map(Number),
        RepeatInterval: Number(form.repeatInterval) || 1,
        SendHour: Number(form.sendHour),
        SendMinute: Number(form.sendMinute),
        // Prefer IANA from GET /Timezones; also send WindowsId for backends that expect it
        IanaName: iana,
        TimeZone: windowsId,
        IncludeAllTemplates: includeAllTemplates,
        TemplateIds: includeAllTemplates
            ? []
            : (form.templateIds || []).map(Number),
        // Only an order the user set is saved; otherwise the blocks keep following the newest audit.
        SaveTemplateOrder: !includeAllTemplates && Boolean(form.templateOrderSaved),
        IncludeAllAuditors: includeAllAuditors,
        SendAnyStatus: Boolean(form.sendAnyStatus),
        AuditorUserIds: includeAllAuditors
            ? []
            : (form.auditorUserIds || []).map(Number),
        Recipients: [
            ...(form.userIds || []).map(userId => ({
                UserId: Number(userId),
            })),
            ...parseExternalEmails(form.externalEmails).map(email => ({
                Email: email,
            })),
        ],
    }
}

export const formatFiltersLabel = item => {
    const tpl = item.IncludeAllTemplates
        ? 'All tpl'
        : `${item.TemplateCount || 0} tpl`
    const aud = item.IncludeAllAuditors ? 'All auditors' : 'Selected auditors'
    return `${tpl} · ${aud}`
}

export const formatRecipientSummary = item => {
    const count = item.RecipientCount || 0
    return `${count} recipients`
}

export const getLastRunBadge = status => {
    switch (status) {
        case 'Delivered':
            return { color: 'green', label: '✓ Delivered' }
        case 'Partial':
            return { color: 'orange', label: '⚠ Partial' }
        case 'Failed':
        case 'Failed-Skipped':
            return { color: 'red', label: '✗ Failed' }
        case 'Skipped-Empty':
        case 'Skipped-Held':
        case 'Skipped-NoRecipients':
            return { color: 'grey', label: '⊘ Skipped' }
        default:
            return { color: 'grey', label: status || '—' }
    }
}

export const getRunTrigger = run => {
    const trigger = run?.Trigger || run?.trigger
    if (trigger) return trigger
    const by = run?.TriggeredBy || run?.triggeredBy
    if (by === 'Manual' || by === 'Test' || by === 'Scheduler') return by
    return 'Scheduler'
}

export const getTriggerBadge = trigger => {
    switch (trigger) {
        case 'Manual':
            return { color: 'orange', label: 'Manual' }
        case 'Test':
            return { color: 'grey', label: 'Test' }
        default:
            return { color: 'grey', label: 'Scheduler' }
    }
}

export const extractApiError = err => {
    const body = err?.error
    if (typeof body === 'string') return body
    if (body?.ErrorMessage) return body.ErrorMessage
    if (body?.Message) return body.Message
    if (body?.message) return body.message
    return null
}

export const getApiErrorField = err => {
    const body = err?.error
    return body?.Field || null
}

export const getApiErrorCode = err => {
    const body = err?.error
    return body?.ErrorCode || body?.errorCode || null
}

export const isCompanyAdminUser = user => {
    if (!user) return false
    if (user.IsSuperAdmin === true) return true
    if (user.IsAdmin === true) return true
    const role = String(user.Role || '').toLowerCase()
    if (role === 'admin' || role === 'superadmin' || role === 'super admin') {
        return true
    }
    // OwnerTypeId 1 = Admin (Inspector / regular users are 2+)
    if (Number(user.OwnerType?.OwnerTypeId) === 1) return true
    // RoleId 3 = Admin in this app (RoleId 1 is Inspector — do not treat as admin)
    if (Number(user.RoleId ?? user.RoleID) === 3) return true
    return (user.Companies || []).some(c => {
        if (c.IsAdmin === true) return true
        return Number(c.RoleId ?? c.RoleID) === 3
    })
}

/** Report Emails: Admin and Super Admin only. */
export const canManageConsolidatedReports = user => {
    if (!user) return false
    if (user.IsSuperAdmin === true) return true
    if (user.IsAdmin === true) return true
    const role = String(user.Role || '').toLowerCase()
    if (role === 'admin' || role === 'superadmin' || role === 'super admin') {
        return true
    }
    if (Number(user.RoleId ?? user.RoleID) === 3) return true
    if (Number(user.OwnerType?.OwnerTypeId) === 1) return true
    return false
}

export const exportRunsToCsv = (runs, timezone) => {
    const headers = [
        'Schedule',
        'Fired at',
        'Window',
        'Reports',
        'Recipients',
        'Trigger',
        'Status',
    ]
    const rows = (runs || []).map(run => [
        run.ScheduleName || '',
        formatUtcInTimezone(run.FiredAtUtc, timezone),
        formatRunWindow(run, timezone),
        run.ReportsIncluded ?? '',
        run.RecipientCount ?? '',
        run.Trigger || '',
        run.Status || '',
    ])
    const csv = [headers, ...rows]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'consolidated-report-runs.csv'
    link.click()
    URL.revokeObjectURL(url)
}
